import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { useChat } from '../../hooks/useWebSocket';
import { useAppStore } from '../../stores/appStore';
import ChatMessage from './ChatMessage';

const ChatBot = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { isOffline } = useAppStore();
  const {
    messages,
    isActyTyping,
    isChatOpen,
    isConnected,
    sendMessage,
    openChat,
    closeChat,
    onTypingStart,
    onTypingStop,
  } = useChat();

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isActyTyping]);

  // Focus input when chat opens
  useEffect(() => {
    if (isChatOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [isChatOpen]);

  // Typing indicator timeout
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    
    if (isTyping) {
      onTypingStart();
      timeout = setTimeout(() => {
        setIsTyping(false);
        onTypingStop();
      }, 1000);
    } else {
      onTypingStop();
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isTyping, onTypingStart, onTypingStop]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInput(value);
    
    if (value.length > 0 && !isTyping) {
      setIsTyping(true);
    } else if (value.length === 0 && isTyping) {
      setIsTyping(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!input.trim() || isOffline) return;

    const message = input.trim();
    setInput('');
    setIsTyping(false);
    
    await sendMessage(message);
  };

  const handleExampleClick = (example: string) => {
    setInput(example);
    inputRef.current?.focus();
  };

  const exampleQuestions = [
    t('chat.examples.events'),
    t('chat.examples.location'),
    t('chat.examples.speakers'),
    t('chat.examples.schedule'),
  ];

  return (
    <>
      {/* Chat Toggle Button */}
      <AnimatePresence>
        {!isChatOpen && (
          <motion.button
            className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 text-white rounded-full shadow-manga flex items-center justify-center z-40"
            onClick={openChat}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <MessageCircle className="w-6 h-6" />
            
            {/* Bounce animation every 10 seconds */}
            <motion.div
              className="absolute inset-0 rounded-full"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ 
                duration: 0.6, 
                repeat: Infinity, 
                repeatDelay: 10,
                ease: 'easeInOut'
              }}
            />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Modal */}
      <AnimatePresence>
        {isChatOpen && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center sm:justify-end z-50 p-4 sm:pr-6 sm:pb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeChat}
          >
            <motion.div
              className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:w-96 h-full sm:h-[600px] max-h-[80vh] flex flex-col shadow-2xl"
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-4 rounded-t-2xl sm:rounded-t-2xl flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                    <span className="text-lg">🎭</span>
                  </div>
                  <div>
                    <h3 className="font-semibold">Acty</h3>
                    <p className="text-sm opacity-90">
                      {isOffline
                        ? t('chat.offline')
                        : isConnected
                        ? t('chat.assistant')
                        : t('chat.disconnected')
                      }
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeChat}
                  className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {messages.map((message) => (
                  <ChatMessage key={message.id} message={message} />
                ))}

                {/* Typing Indicator */}
                {isActyTyping && (
                  <motion.div
                    className="flex items-start space-x-3"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm">🎭</span>
                    </div>
                    <div className="bg-white rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                      <div className="flex items-center space-x-1">
                        <div className="flex space-x-1">
                          <motion.div
                            className="w-2 h-2 bg-gray-400 rounded-full"
                            animate={{ opacity: [1, 0.3, 1] }}
                            transition={{ duration: 1, repeat: Infinity, delay: 0 }}
                          />
                          <motion.div
                            className="w-2 h-2 bg-gray-400 rounded-full"
                            animate={{ opacity: [1, 0.3, 1] }}
                            transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
                          />
                          <motion.div
                            className="w-2 h-2 bg-gray-400 rounded-full"
                            animate={{ opacity: [1, 0.3, 1] }}
                            transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 ml-2">
                          {t('chat.typing')}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Example Questions */}
                {messages.length === 1 && !isActyTyping && (
                  <motion.div
                    className="space-y-2"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <p className="text-sm text-gray-500 text-center mb-3">
                      {t('chat.exampleQuestions')}
                    </p>
                    {exampleQuestions.map((example, index) => (
                      <motion.button
                        key={example}
                        className="w-full text-left p-3 bg-white hover:bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-700 transition-colors"
                        onClick={() => handleExampleClick(example)}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 + 0.6 }}
                        whileHover={{ x: 5 }}
                      >
                        {example}
                      </motion.button>
                    ))}
                  </motion.div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="p-4 border-t border-gray-200 bg-white rounded-b-2xl">
                {isOffline && (
                  <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg text-center">
                    <p className="text-sm text-red-600">{t('chat.offline')}</p>
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="flex items-end space-x-2">
                  <div className="flex-1">
                    <input
                      ref={inputRef}
                      type="text"
                      value={input}
                      onChange={handleInputChange}
                      placeholder={isOffline ? t('chat.offlinePlaceholder') : t('chat.placeholder')}
                      disabled={isOffline}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                      maxLength={500}
                    />
                  </div>
                  
                  <motion.button
                    type="submit"
                    disabled={!input.trim() || isOffline}
                    className="p-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                    whileHover={{ scale: input.trim() && !isOffline ? 1.05 : 1 }}
                    whileTap={{ scale: input.trim() && !isOffline ? 0.95 : 1 }}
                  >
                    <Send className="w-5 h-5" />
                  </motion.button>
                </form>
                
                <p className="text-xs text-gray-500 mt-2 text-center">
                  {t('chat.poweredBy')}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatBot;